import React, { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { Text, useMatcapTexture, Octahedron } from '@react-three/drei'

// ─── ThinFilmFresnelMap (inline) ─────────────────────────────────────────────
class ThinFilmFresnelMap extends THREE.DataTexture {
  constructor(thickness = 410, refractiveIndexFilm = 1.4, refractiveIndexBase = 2.5, size = 512) {
    const data = new Uint8Array(size * 4)
    for (let i = 0; i < size; i++) {
      const t = i / size
      const hue = (t * 360 + thickness / 3) % 360
      const h = hue / 360
      const q = 0.7
      const p = 0.1
      const toRGB = (t) => {
        if (t < 0) t += 1; if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }
      const idx = i * 4
      data[idx]     = Math.round(toRGB(h + 1/3) * 255)
      data[idx + 1] = Math.round(toRGB(h) * 255)
      data[idx + 2] = Math.round(toRGB(h - 1/3) * 255)
      data[idx + 3] = 255
    }
    super(data, size, 1, THREE.RGBAFormat)
    this.needsUpdate = true
    this.wrapS = this.wrapT = THREE.RepeatWrapping
  }
}

// ─── Diamond positions (replaces data.js) ────────────────────────────────────
const DIAMOND_POSITIONS = Array.from({ length: 20 }, (_, i) => {
  const angle = (i / 20) * Math.PI * 2
  const radius = 3 + (i % 3) * 1.5
  return {
    position: [Math.cos(angle) * radius, Math.sin(angle * 0.7) * 1.5, Math.sin(angle) * radius * 0.5],
    rotation: [Math.random() * Math.PI, Math.random() * Math.PI, 0],
  }
})

// ─── useSlerp ─────────────────────────────────────────────────────────────────
function useSlerp() {
  const ref = useRef()
  const mouse = useRef({ x: 0, y: 0 })
  const target = useRef(new THREE.Quaternion())

  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 0.3
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 0.3
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  useFrame(() => {
    if (!ref.current) return
    const euler = new THREE.Euler(-mouse.current.y, mouse.current.x, 0)
    target.current.setFromEuler(euler)
    ref.current.quaternion.slerp(target.current, 0.05)
  })

  return ref
}

// ─── RenderTarget ─────────────────────────────────────────────────────────────
function useRenderTarget() {
  const renderTarget = useMemo(() => new THREE.WebGLCubeRenderTarget(256, {
    format: THREE.RGBAFormat,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
  }), [])
  const cubeCamera = useMemo(() => new THREE.CubeCamera(0.1, 1000, renderTarget), [renderTarget])
  const { gl, scene } = useThree()

  useFrame(() => {
    cubeCamera.update(gl, scene)
  })

  return [cubeCamera, renderTarget]
}

// ─── Diamond mesh (octahedron as diamond shape) ───────────────────────────────
function Diamond({ matcap, position, rotation }) {
  const ref = useRef()
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.y += 0.001
      ref.current.rotation.z += 0.01
    }
  })
  return (
    <mesh ref={ref} position={position} rotation={rotation} scale={[0.5, 0.8, 0.5]}>
      <octahedronGeometry args={[1, 0]} />
      <meshMatcapMaterial matcap={matcap} transparent opacity={0.9} color="#14CEFF" />
    </mesh>
  )
}

function Diamonds() {
  const [matcap] = useMatcapTexture('2E763A_78A0B7_B3D1CF_14F209', 512)
  return (
    <group>
      {DIAMOND_POSITIONS.map((d, i) => (
        <Diamond key={i} matcap={matcap} position={d.position} rotation={d.rotation} />
      ))}
    </group>
  )
}

function Title({ texture, map }) {
  return (
    <group position={[0, 0, -5]}>
      <Text
        depthTest={false}
        material-toneMapped={false}
        textAlign="center"
        fontSize={3}
        lineHeight={1}
        maxWidth={10}
        font="https://fonts.gstatic.com/s/monoton/v10/5h1aiZUrOngCibe4TkHLRA.woff"
      >
        {`WEB\nDESIGN`}
        <meshPhysicalMaterial
          envMap={texture}
          map={map}
          roughness={0}
          metalness={1}
          color="#FFFFFF"
        />
      </Text>
    </group>
  )
}

// ─── Scene ────────────────────────────────────────────────────────────────────
export default function Scene2() {
  const [, renderTarget] = useRenderTarget()
  const thinFilmMap = useMemo(() => new ThinFilmFresnelMap(410, 1.4, 2.5, 1024), [])
  const group = useSlerp()
  const [matcap] = useMatcapTexture('BA5DBA_F2BEF2_E69BE6_DC8CDC', 512)

  return (
    <>
      <Octahedron args={[20, 4, 4]} position={[0, 0, -5]}>
        <meshMatcapMaterial matcap={matcap} side={THREE.BackSide} color="#FFFFFF" />
      </Octahedron>
      <group ref={group}>
        <Diamonds />
        <Title texture={renderTarget.texture} map={thinFilmMap} />
      </group>
      <ambientLight intensity={0.6} />
      <pointLight position={[5, 5, 5]} intensity={1.5} color="#ff88ff" />
      <pointLight position={[-5, -5, 5]} intensity={1} color="#88ffff" />
    </>
  )
}
